/* =============================================================
   Login.jsx — Authentication page
   -------------------------------------------------------------
   Shared login screen for both admin and student users.
   Two modes selectable via tabs at the top of the card:

     Admin tab   — email + password → redirects to /dashboard
     Student tab — email only → sends a magic link to their inbox

   On successful admin login, redirects based on the role
   returned by AuthContext (future-proof for multi-role setups).

   ✓ DONE (backend integration):
   - Removed DEMO_USERS and demo quick-fill buttons
   - handleSubmit is now async — calls real Supabase auth
   - Admin flow: await login(email, password)
   - Student flow: await sendMagicLink(email) → shows confirmation
   - Errors come from Supabase and are displayed in the error box
   ============================================================= */

import { useState } from 'react';
import { useAuth } from '../context/AuthContext';


/* -------------------------------------------------------------
   LOGIN COMPONENT
   ------------------------------------------------------------- */
export default function Login() {
  const { login, sendMagicLink }    = useAuth();

  /* ── form state ── */
  const [mode,     setMode]     = useState('admin');   // 'admin' | 'student'
  const [email,    setEmail]    = useState('');
  const [password, setPassword] = useState('');
  const [error,    setError]    = useState('');
  const [loading,  setLoading]  = useState(false);
  const [shake,    setShake]    = useState(false);
  const [showPass, setShowPass] = useState(false);

  // True after a magic link is successfully sent to the student.
  const [magicLinkSent, setMagicLinkSent] = useState(false);


  /* ─── triggerShake ───────────────────────────────────────── */
  // Briefly applies the shake CSS animation on the card when
  // the user enters invalid credentials.
  const triggerShake = () => {
    setShake(true);
    setTimeout(() => setShake(false), 500);
  };


  /* ─── switchMode ─────────────────────────────────────────── */
  // Resets all form state when switching between Admin / Student.
  const switchMode = (newMode) => {
    setMode(newMode);
    setEmail('');
    setPassword('');
    setError('');
    setMagicLinkSent(false);
  };


  /* ─── handleSubmit ───────────────────────────────────────── */
  // Admin mode:   calls login(email, password), redirects on success.
  // Student mode: calls sendMagicLink(email), shows confirmation.
  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');

    // Basic validation
    if (!email.trim()) {
      setError('Please enter your email address.');
      triggerShake();
      return;
    }
    if (mode === 'admin' && !password) {
      setError('Please enter your password.');
      triggerShake();
      return;
    }

    setLoading(true);

    try {
      if (mode === 'admin') {
        /* ── Admin: email + password login ──
           login() only calls signInWithPassword — no navigation here.
           Once Supabase confirms the session, onAuthStateChange fires,
           user state updates, and LoginRoute redirects automatically.
           The button stays in "Signing in..." until the redirect happens. */
        await login(email, password);

      } else {
        /* ── Student: send magic link ── */
        await sendMagicLink(email);
        setMagicLinkSent(true);
        setLoading(false);
      }

    } catch (err) {
      // Login failed — show the error and re-enable the button.
      setError(err.message ?? 'Something went wrong. Please try again.');
      triggerShake();
      setLoading(false);
    }
  };


  return (
    <div className="min-h-screen bg-[#f8f9ff] flex flex-col items-center justify-center px-4">

      {/* ── Logo ── */}
      <div className="flex flex-col items-center mb-10">
        <div className="w-20 h-20 rounded-3xl overflow-hidden shadow-md mb-4">
          <img src="/logo.jpg" alt="Unixplore" className="w-full h-full object-cover scale-110" />
        </div>
        <p className="text-xs font-semibold text-slate-400 uppercase tracking-widest">Unixplore</p>
      </div>

      {/* ── Login card ── */}
      <div
        className={`bg-white rounded-3xl shadow-sm border border-slate-100 p-10 w-full max-w-md transition-all ${
          shake ? 'animate-shake' : ''
        }`}
      >
        <h1 className="font-display text-2xl font-semibold text-[#0b1c30] text-center mb-1">
          Welcome back
        </h1>
        <p className="text-[#464555] text-sm text-center mb-6">
          Sign in to access your portal
        </p>

        {/* ── Mode tabs: Admin / Student ── */}
        <div className="flex bg-slate-100 rounded-xl p-1 mb-8">
          {[
            { key: 'admin',   label: 'Admin',   icon: 'admin_panel_settings' },
            { key: 'student', label: 'Student', icon: 'school' },
          ].map(tab => (
            <button
              key={tab.key}
              type="button"
              onClick={() => switchMode(tab.key)}
              className={`flex-1 flex items-center justify-center gap-2 py-2 rounded-lg text-sm font-semibold transition-all ${
                mode === tab.key
                  ? 'bg-white text-[#0b1c30] shadow-sm'
                  : 'text-slate-400 hover:text-slate-600'
              }`}
            >
              <span className="material-symbols-outlined text-[16px]">{tab.icon}</span>
              {tab.label}
            </button>
          ))}
        </div>

        {/* ── Magic link sent confirmation (student only) ── */}
        {magicLinkSent ? (
          <div className="flex flex-col items-center text-center py-6 gap-4">
            <div className="w-14 h-14 rounded-2xl bg-emerald-50 flex items-center justify-center">
              <span className="material-symbols-outlined text-emerald-500 text-[28px]">mark_email_read</span>
            </div>
            <div>
              <p className="font-semibold text-[#0b1c30] mb-1">Check your inbox</p>
              <p className="text-sm text-slate-500">
                We sent a login link to <span className="font-medium text-[#0b1c30]">{email}</span>.
                Click it to sign in — no password needed.
              </p>
            </div>
            <button
              type="button"
              onClick={() => { setMagicLinkSent(false); setEmail(''); }}
              className="text-xs text-indigo-500 hover:text-indigo-700 font-medium transition-colors"
            >
              Use a different email
            </button>
          </div>

        ) : (
          /* ── Login form ── */
          <form onSubmit={handleSubmit} noValidate>

            {/* Email field */}
            <div className="mb-4">
              <label className="block text-xs font-semibold text-slate-500 uppercase tracking-wide mb-1.5">
                Email address
              </label>
              <div className="relative">
                <span className="material-symbols-outlined absolute left-3 top-1/2 -translate-y-1/2 text-slate-400 text-[18px]">
                  mail
                </span>
                <input
                  type="email"
                  value={email}
                  onChange={e => { setEmail(e.target.value); setError(''); }}
                  placeholder="your@email.com"
                  autoComplete="email"
                  className={`w-full pl-10 pr-4 py-3 text-sm border rounded-xl focus:outline-none focus:ring-2 focus:ring-indigo-200 focus:border-indigo-300 text-[#0b1c30] placeholder-slate-400 transition ${
                    error ? 'border-red-300 bg-red-50/30' : 'border-slate-200'
                  }`}
                />
              </div>
            </div>

            {/* Password field — admin only */}
            {mode === 'admin' && (
              <div className="mb-6">
                <label className="block text-xs font-semibold text-slate-500 uppercase tracking-wide mb-1.5">
                  Password
                </label>
                <div className="relative">
                  <span className="material-symbols-outlined absolute left-3 top-1/2 -translate-y-1/2 text-slate-400 text-[18px]">
                    lock
                  </span>
                  <input
                    type={showPass ? 'text' : 'password'}
                    value={password}
                    onChange={e => { setPassword(e.target.value); setError(''); }}
                    placeholder="••••••••"
                    autoComplete="current-password"
                    className={`w-full pl-10 pr-10 py-3 text-sm border rounded-xl focus:outline-none focus:ring-2 focus:ring-indigo-200 focus:border-indigo-300 text-[#0b1c30] placeholder-slate-400 transition ${
                      error ? 'border-red-300 bg-red-50/30' : 'border-slate-200'
                    }`}
                  />
                  <button
                    type="button"
                    onClick={() => setShowPass(v => !v)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600 transition-colors"
                  >
                    <span className="material-symbols-outlined text-[18px]">
                      {showPass ? 'visibility_off' : 'visibility'}
                    </span>
                  </button>
                </div>
              </div>
            )}

            {/* Hint text for student mode */}
            {mode === 'student' && (
              <p className="text-xs text-slate-400 mb-6 flex items-center gap-1.5">
                <span className="material-symbols-outlined text-[14px]">info</span>
                Enter your university email and we'll send you a secure login link.
              </p>
            )}

            {/* Error message */}
            {error && (
              <div className="mb-4 flex items-start gap-2 px-4 py-3 bg-red-50 border border-red-100 rounded-xl">
                <span className="material-symbols-outlined text-red-400 text-[16px] flex-shrink-0 mt-0.5">error</span>
                <p className="text-xs text-red-600">{error}</p>
              </div>
            )}

            {/* Submit button */}
            <button
              type="submit"
              disabled={loading}
              className="w-full py-3 bg-[#4343d5] text-white font-semibold text-sm rounded-xl hover:bg-indigo-700 transition-colors flex items-center justify-center gap-2 disabled:opacity-60"
            >
              {loading ? (
                <>
                  <span className="w-4 h-4 border-2 border-white/40 border-t-white rounded-full animate-spin" />
                  {mode === 'student' ? 'Sending link…' : 'Signing in…'}
                </>
              ) : (
                <>
                  {mode === 'student' ? 'Send login link' : 'Sign in'}
                  <span className="material-symbols-outlined text-[18px]">
                    {mode === 'student' ? 'send' : 'arrow_forward'}
                  </span>
                </>
              )}
            </button>

          </form>
        )}
      </div>

      <p className="text-xs text-slate-400 mt-6">Unixplore © 2026 · All rights reserved</p>

      {/* Shake keyframe animation */}
      <style>{`
        @keyframes shake {
          0%, 100% { transform: translateX(0); }
          20%       { transform: translateX(-6px); }
          40%       { transform: translateX(6px); }
          60%       { transform: translateX(-4px); }
          80%       { transform: translateX(4px); }
        }
        .animate-shake { animation: shake 0.45s ease-in-out; }
      `}</style>
    </div>
  );
}
